import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { TILE, surfaceLift } from '../../shared/geo.js';
import { resolveCircle } from '../../shared/collision.js';
import { hash2 } from '../../shared/rng.js';
import { Instances } from './instances.js';
import { loadModel, loadVehicle } from '../world/models.js';
import { yawRoll } from '../physics/physics.js';

// top speeds in metres per second on tar, on earth roads and off the road
export const VEHICLES = {
  taxi: { name: 'taxi', id: 1, tar: 23, earth: 13, rough: 7, accel: 6.5, brake: 15, turn: 1.9, radius: 1.35, seat: 0 },
  bike: { name: 'bike', id: 2, tar: 21, earth: 15, rough: 9, accel: 8, brake: 13, turn: 2.6, radius: 0.7, seat: 0.62 },
  bus: { name: 'minibus', id: 3, tar: 20, earth: 11, rough: 6, accel: 4.5, brake: 13, turn: 1.5, radius: 1.6, seat: 0 },
  car: { name: 'car', id: 4, tar: 25, earth: 13, rough: 7, accel: 7, brake: 15, turn: 1.9, radius: 1.3, seat: 0 },
  pickup: { name: 'pickup', id: 5, tar: 24, earth: 14, rough: 9, accel: 6, brake: 14, turn: 1.8, radius: 1.4, seat: 0 },
  coach: { name: 'coach', id: 6, tar: 22, earth: 10, rough: 5, accel: 3, brake: 12, turn: 1.1, radius: 2.6, seat: 0 },
};
const BY_ID = { 1: 'taxi', 2: 'bike', 3: 'bus', 4: 'car', 5: 'pickup', 6: 'coach' };
const STEER = 0.45; // radians the front wheels turn at full lock
// paints of the private cars and pickups: mostly white and silver, as on the road here
const PAINTS = ['#f2f2f0', '#f2f2f0', '#f2f2f0', '#c9cbcf', '#c9cbcf', '#2b2b2e', '#1f3f7a', '#7a1f1f', '#2f5f32', '#d8d0a8', '#8a6a4a'].map((h) => new THREE.Color(h));
export function paintFor(seed) {
  return PAINTS[Math.floor(seed * PAINTS.length) % PAINTS.length];
}
const PAINTABLE = { car: true, pickup: true };

function colored(geometry, hex, x, y, z) {
  geometry.translate(x, y, z);
  const g = geometry.index ? geometry.toNonIndexed() : geometry;
  const c = new THREE.Color(hex);
  const n = g.attributes.position.count;
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    col[i * 3] = c.r;
    col[i * 3 + 1] = c.g;
    col[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}

const box = (w, h, d, hex, x, y, z) => colored(new THREE.BoxGeometry(w, h, d), hex, x, y, z);
const wheel = (r, w, x, y, z) => colored(new THREE.CylinderGeometry(r, r, w, 10).rotateZ(Math.PI / 2), '#1c1c1e', x, y, z);

let SHAPES = null;
let MATERIAL = null;

function shapes() {
  if (SHAPES) return SHAPES;
  MATERIAL = new THREE.MeshLambertMaterial({ vertexColors: true });
  SHAPES = {
    // the yellow shared taxi of every Cameroonian town
    taxi: mergeGeometries([
      box(1.72, 0.62, 4.2, '#f2b705', 0, 0.62, 0),
      box(1.56, 0.56, 2.1, '#f2b705', 0, 1.2, -0.2),
      box(1.6, 0.4, 1.9, '#26323f', 0, 1.2, -0.2),
      box(1.5, 0.06, 1.7, '#f2b705', 0, 1.5, -0.2),
      box(0.5, 0.16, 0.24, '#f7eed9', 0, 1.6, -0.2),
      box(1.76, 0.2, 0.2, '#3a3a3e', 0, 0.42, 2.08),
      box(1.76, 0.2, 0.2, '#3a3a3e', 0, 0.42, -2.08),
      box(0.3, 0.14, 0.06, '#fff4c0', 0.6, 0.72, 2.11),
      box(0.3, 0.14, 0.06, '#fff4c0', -0.6, 0.72, 2.11),
      box(0.3, 0.14, 0.06, '#d8402f', 0.6, 0.72, -2.11),
      box(0.3, 0.14, 0.06, '#d8402f', -0.6, 0.72, -2.11),
      wheel(0.34, 0.24, 0.82, 0.34, 1.3),
      wheel(0.34, 0.24, -0.82, 0.34, 1.3),
      wheel(0.34, 0.24, 0.82, 0.34, -1.3),
      wheel(0.34, 0.24, -0.82, 0.34, -1.3),
    ]),
    // a commercial bike: yellow tank, as the council requires
    bike: mergeGeometries([
      wheel(0.31, 0.12, 0, 0.31, 0.7),
      wheel(0.31, 0.12, 0, 0.31, -0.66),
      box(0.16, 0.2, 1.3, '#b02a26', 0, 0.52, 0),
      box(0.3, 0.24, 0.5, '#f2b705', 0, 0.76, 0.26),
      box(0.3, 0.1, 0.72, '#1c1c1e', 0, 0.76, -0.34),
      box(0.62, 0.06, 0.06, '#3a3a3e', 0, 1.02, 0.62),
      box(0.1, 0.5, 0.1, '#3a3a3e', 0, 0.74, 0.66),
      box(0.14, 0.12, 0.08, '#fff4c0', 0, 0.9, 0.78),
    ]),
    // plain blocks of the right size stand in for the other kinds until their models arrive
    car: box(1.7, 1.4, 4.3, '#c9c9c9', 0, 0.7, 0),
    pickup: box(1.74, 1.6, 4.6, '#d8d8d8', 0, 0.8, 0),
    bus: box(1.72, 2.0, 4.7, '#f1f0ea', 0, 1.0, 0),
    coach: box(2.5, 3.4, 10.6, '#efe7cf', 0, 1.7, 0),
  };
  return SHAPES;
}

// The driven vehicle's own mesh, its wheels as children so they can turn. paint: the colour of a
// paintable kind (car, pickup), else its kit colours stay.
export function vehicleMesh(type, paint = null) {
  requestModels();
  let material = MATERIAL;
  if (PAINTABLE[type]) {
    material = MATERIAL.clone();
    material.color.copy(paint || paintFor(Math.random()));
  }
  const mesh = new THREE.Mesh(shapes()[type], material);
  mesh.castShadow = true;
  addWheels(mesh, type);
  addLamps(mesh, type);
  liveShapes.add({ type, mesh, own: true });
  return mesh;
}

// Switches the lamps of every vehicle with its own mesh (driven, other players') to the night.
export function lampsAtNight(dark) {
  lampMaterial().opacity = Math.min(1, dark * 1.6);
  for (const entry of liveShapes) {
    const lamps = entry.own && entry.mesh.userData.lamps;
    if (lamps) lamps.visible = dark > 0.25;
  }
}

function addWheels(mesh, type) {
  const w = WHEELS[type];
  if (!w || mesh.userData.wheels) return;
  mesh.userData.wheels = w.wheels.map((o) => {
    const m = new THREE.Mesh(w.geometry, MATERIAL);
    m.position.set(o.x, o.y, o.z);
    m.castShadow = true;
    m.userData.front = o.front;
    mesh.add(m);
    return m;
  });
}

// The shared shape of a vehicle type and the material every vehicle uses.
export function vehicleShape(type) {
  return { geometry: shapes()[type], material: MATERIAL };
}

const POOL = { taxi: 400, bike: 500, car: 160, bus: 100, pickup: 80, coach: 24 }; // vehicles of each kind drawn as instances at once
// the models made in Blender through the MCP (taxi, saloon, pickup, okada, Hiace-type minibus and
// coach; see docs/M2_SLICE.md), all facing +z. Every kind but the bike has its wheels apart, to turn.
const MODELS = {
  taxi: ['car-taxi-b', { scale: 1 }],
  bike: ['bike-okada', { scale: 1 }],
  car: ['car-sedan-b', { scale: 1, paintable: true }],
  pickup: ['car-pickup-b', { scale: 1, paintable: true }],
  bus: ['car-minibus', { scale: 1 }],
  coach: ['car-coach', { scale: 1 }],
};
const WHEELS = {}; // type -> { geometry, wheels: [{ x, y, z, front }], radius } once its model is in
const LAMPS = {}; // type -> the glowing head and tail lamps of one vehicle, in its own frame
let LAMP_MATERIAL = null;
function lampMaterial() {
  if (!LAMP_MATERIAL) LAMP_MATERIAL = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false });
  return LAMP_MATERIAL;
}

// Four lamps at the corners of a body: warm white ahead, red behind, each a small cross of two
// quads that reads as a glow from any side. From the body's extent, so every kind fits.
function lampGeometry(body) {
  body.computeBoundingBox();
  const b = body.boundingBox;
  const w = b.max.x - b.min.x;
  const h = b.max.y - b.min.y;
  const parts = [];
  const lamp = (x, y, z, hex) => {
    const a = new THREE.PlaneGeometry(0.42, 0.2).translate(x, y, z);
    const c = new THREE.PlaneGeometry(0.42, 0.2).rotateY(Math.PI / 2).translate(x, y, z);
    for (const g of [a, c]) parts.push(colored(g, hex, 0, 0, 0));
  };
  for (const sx of [-1, 1]) {
    lamp(sx * (w / 2 - 0.32), h * 0.42, b.max.z - 0.03, '#fff1c2');
    lamp(sx * (w / 2 - 0.3), h * 0.44, b.min.z + 0.03, '#ff3020');
  }
  // the headlights' throw on the road: a dim warm trapezoid on the ground ahead, fading out
  const beam = new THREE.BufferGeometry();
  const z0 = b.max.z + 0.2;
  const z1 = b.max.z + 11;
  beam.setAttribute('position', new THREE.Float32BufferAttribute([-w * 0.45, 0.07, z0, w * 0.45, 0.07, z0, w * 1.1, 0.07, z1, -w * 1.1, 0.07, z1], 3));
  beam.setAttribute('normal', new THREE.Float32BufferAttribute([0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0], 3));
  beam.setAttribute('color', new THREE.Float32BufferAttribute([0.35, 0.3, 0.18, 0.35, 0.3, 0.18, 0, 0, 0, 0, 0, 0], 3));
  beam.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
  beam.setIndex([0, 2, 1, 0, 3, 2]);
  parts.push(beam.toNonIndexed());
  const merged = mergeGeometries(parts);
  // double-sided through geometry: the material stays single-sided like the rest
  const back = merged.clone();
  back.scale(1, 1, 1);
  const index = back.index;
  if (index) {
    for (let i = 0; i < index.count; i += 3) {
      const t = index.getX(i + 1);
      index.setX(i + 1, index.getX(i + 2));
      index.setX(i + 2, t);
    }
  } else {
    const p = back.attributes.position;
    for (let i = 0; i < p.count; i += 3) {
      const x = p.getX(i + 1), y = p.getY(i + 1), z = p.getZ(i + 1);
      p.setXYZ(i + 1, p.getX(i + 2), p.getY(i + 2), p.getZ(i + 2));
      p.setXYZ(i + 2, x, y, z);
    }
  }
  return mergeGeometries([merged, back]);
}

function addLamps(mesh, type) {
  const g = LAMPS[type];
  if (!g || mesh.userData.lamps) return;
  const lamps = new THREE.Mesh(g, lampMaterial());
  lamps.visible = false;
  mesh.add(lamps);
  mesh.userData.lamps = lamps;
}
const liveShapes = new Set(); // meshes drawing a vehicle type, to swap when a model arrives
const wheelListeners = new Set(); // called with the type when its wheels arrive
let modelsRequested = false;
function requestModels() {
  if (modelsRequested) return;
  modelsRequested = true;
  for (const [type, [name, options]] of Object.entries(MODELS)) {
    const swap = (geometry) => {
      shapes()[type] = geometry;
      for (const entry of liveShapes) {
        if (entry.type !== type) continue;
        entry.mesh.geometry = geometry;
        entry.mesh.computeBoundingSphere();
        if (entry.own) addWheels(entry.mesh, type);
      }
    };
    if (type === 'bike') {
      loadModel(name, options).then((geometry) => geometry && swap(geometry));
      continue;
    }
    loadVehicle(name, options).then((v) => {
      if (!v) return;
      WHEELS[type] = { geometry: v.wheel, wheels: v.wheels, radius: v.radius };
      LAMPS[type] = lampGeometry(v.body);
      swap(v.body);
      for (const entry of liveShapes) if (entry.type === type && entry.own) addLamps(entry.mesh, type);
      for (const fn of wheelListeners) fn(type);
    });
  }
}
const tmp = new THREE.Object3D();
const ZERO = new THREE.Matrix4().makeScale(0, 0, 0);
const tmpWheel = new THREE.Object3D();
const tmpMatrix = new THREE.Matrix4();

export function vehicleTypeOf(id) {
  return BY_ID[id] || null;
}

// Parked taxis and bikes along the roads, and the one the player is driving.
export class Vehicles {
  constructor(scene, map, chunks) {
    this.scene = scene;
    this.map = map;
    this.chunks = chunks;
    this.parked = new Map(); // tile key -> list of { type, x, z, yaw, slot }
    this.driving = null; // { type, def, x, z, yaw, speed, mesh }
    this.hits = [];
    // every parked, passing or queuing vehicle of a kind is one instance in one draw call, and
    // its wheels four more in the kind's wheel pool, at slots four times its own
    this.pool = {};
    this.wheels = {};
    this.lamps = {}; // type -> Instances of lamp glows, one per vehicle at the vehicle's slot
    this.night = 0;
    requestModels();
    for (const type of Object.keys(POOL)) {
      const s = vehicleShape(type);
      this.pool[type] = new Instances(scene, s.geometry, s.material, POOL[type], !!PAINTABLE[type]);
      liveShapes.add({ type, mesh: this.pool[type].mesh });
    }
    wheelListeners.add((type) => this.wheelsArrived(type));
    for (const type of Object.keys(WHEELS)) this.wheelsArrived(type);
    this.obstacles = null; // (x, z, list) => adds other things to bump into, such as traffic
    this.physics = null; // set by the game when ?physics=1
  }

  wheelsArrived(type) {
    if (this.wheels[type] || !WHEELS[type]) return;
    const w = WHEELS[type];
    const pool = new Instances(this.scene, w.geometry, MATERIAL, POOL[type] * w.wheels.length);
    pool.clear();
    this.wheels[type] = pool;
    if (LAMPS[type]) {
      const lamps = new Instances(this.scene, LAMPS[type], lampMaterial(), POOL[type]);
      lamps.clear();
      lamps.mesh.castShadow = false;
      lamps.mesh.visible = this.night > 0.25;
      this.lamps[type] = lamps;
    }
    // the parked ones stand still, so their wheels are set once, now
    for (const list of this.parked.values()) for (const v of list) if (v.type === type) this.place(v);
  }

  // Tints a paintable vehicle's instance its own colour.
  paint(v) {
    if (v.paint && v.slot >= 0) this.pool[v.type].tint(v.slot, v.paint);
  }

  // Gives a vehicle's instance slot back, wheels and all.
  release(type, slot) {
    this.pool[type].remove(slot);
    const w = WHEELS[type];
    const wheels = this.wheels[type];
    if (this.lamps[type] && slot >= 0) this.lamps[type].remove(slot);
    if (!w || !wheels || slot < 0) return;
    for (let i = 0; i < w.wheels.length; i++) wheels.remove(slot * w.wheels.length + i);
  }

  // dark: 0 by day to 1 at night. Lamps show on every vehicle once the light has gone.
  setNight(dark) {
    this.night = dark;
    lampsAtNight(dark);
    for (const lamps of Object.values(this.lamps)) lamps.mesh.visible = dark > 0.25;
  }

  // Turns a vehicle's wheels on for the way it has moved this frame.
  roll(v, dt) {
    const w = WHEELS[v.type];
    if (!w) return;
    v.spin = ((v.spin || 0) + ((v.speed || 0) * dt) / w.radius) % (Math.PI * 2);
  }

  // Sets the wheels of an instanced vehicle from its body matrix, spun and steered.
  placeWheels(v, matrix) {
    const w = WHEELS[v.type];
    const pool = this.wheels[v.type];
    if (!w || !pool || v.slot < 0) return;
    const n = w.wheels.length;
    pool.draw((v.slot + 1) * n);
    const lamps = this.lamps[v.type];
    if (lamps) {
      // only vehicles on the move show lamps: the parked ones stand dark
      lamps.draw(v.slot + 1);
      lamps.set(v.slot, v.lit ? matrix : ZERO);
    }
    for (let i = 0; i < n; i++) {
      const o = w.wheels[i];
      tmpWheel.position.set(o.x, o.y, o.z);
      tmpWheel.rotation.set(v.spin || 0, o.front ? v.steer || 0 : 0, 0, 'YXZ');
      tmpWheel.updateMatrix();
      tmpMatrix.multiplyMatrices(matrix, tmpWheel.matrix);
      pool.set(v.slot * n + i, tmpMatrix);
    }
  }

  // The driven vehicle's own wheel meshes.
  turnWheels(v) {
    const wheels = v.mesh && v.mesh.userData.wheels;
    if (!wheels) return;
    for (const m of wheels) m.rotation.set(v.spin || 0, m.userData.front ? v.steer || 0 : 0, 0, 'YXZ');
  }

  // Coaches wait at the motor parks: two at each, the kind the player rides between regions.
  parkCoaches(parks) {
    const old = this.parked.get('coaches');
    if (old) for (const v of old) this.release(v.type, v.slot);
    const list = [];
    for (const k of parks || []) {
      const yaw = hash2(Math.round(k.x), Math.round(k.z), 63) * Math.PI;
      for (const side of [-1, 1]) {
        const v = { type: 'coach', x: k.x + Math.cos(yaw) * side * 4, z: k.z - Math.sin(yaw) * side * 4, yaw, slot: this.pool.coach.add() };
        if (v.slot < 0) continue;
        this.place(v);
        list.push(v);
      }
    }
    this.parked.set('coaches', list);
  }

  // The player's own vehicles stand outside their home, side by side, whenever the server
  // says what they own. Driving one away and parking it elsewhere is kept until the next
  // visit, when it is home again.
  parkOwned(owned, home) {
    const key = 'owned';
    const old = this.parked.get(key) || [];
    const ids = (owned || []).map((v) => v.id).join();
    if (old.length && this.ownedIds === ids && this.ownedHome === `${home.x},${home.z}`) return;
    for (const v of old) this.release(v.type, v.slot);
    this.ownedIds = ids;
    this.ownedHome = `${home.x},${home.z}`;
    const list = [];
    // out of the door and along the front of the house
    const ox = home.dx !== undefined ? home.dx : 0;
    const oz = home.dx !== undefined ? home.dz : 1;
    const px = -oz;
    const pz = ox;
    let i = 0;
    for (const o of owned || []) {
      const type = this.pool[o.type] ? o.type : 'car';
      const slot = this.pool[type].add();
      if (slot < 0) continue;
      const along = 4 + i * 3.6;
      const v = { type, x: home.x + ox * 4.5 + px * along, z: home.z + oz * 4.5 + pz * along, yaw: Math.atan2(px, pz), slot, paint: PAINTABLE[type] ? paintFor(hash2(Math.round(home.x), i, 66)) : null };
      this.paint(v);
      this.place(v);
      list.push(v);
      i++;
    }
    this.parked.set(key, list);
  }

  // Puts a few vehicles beside the tarred roads of a tile. Call when a tile is built.
  addTile(t) {
    if (!t.data) return;
    const list = [];
    const ox = t.tx * TILE;
    const oz = t.tz * TILE;
    for (const r of t.data.r) {
      if (r.c > 4 || r.c === 0 || r.p.length < 4) continue;
      for (let i = 2; i < r.p.length; i += 2) {
        const ax = ox + r.p[i - 2] / 10;
        const az = oz + r.p[i - 1] / 10;
        const bx = ox + r.p[i] / 10;
        const bz = oz + r.p[i + 1] / 10;
        const len = Math.hypot(bx - ax, bz - az);
        if (len < 12) continue;
        const roll = hash2(Math.round(ax), Math.round(az), 61);
        if (roll > (r.s === 0 ? 0.16 : 0.05)) continue;
        const dx = (bx - ax) / len;
        const dz = (bz - az) / len;
        const side = roll * 100 % 1 < 0.5 ? 1 : -1;
        const kind = hash2(Math.round(ax), Math.round(az), 62);
        const type = kind < 0.45 ? 'bike' : kind < 0.72 ? 'taxi' : kind < 0.86 ? 'car' : kind < 0.93 ? 'pickup' : 'bus';
        const off = r.w / 2 + (type === 'bike' ? 0.8 : type === 'bus' ? 1.6 : 1.4);
        const x = ax + dx * len * 0.5 - dz * side * off;
        const z = az + dz * len * 0.5 + dx * side * off;
        // not inside a wall or a compound
        if (resolveCircle(x, z, VEHICLES[type].radius, this.chunks.collidersNear(x, z, this.hits)).hit) continue;
        const v = { type, x, z, yaw: Math.atan2(dx, dz) + (side < 0 ? Math.PI : 0), slot: this.pool[type].add(), paint: PAINTABLE[type] ? paintFor(hash2(Math.round(ax), Math.round(az), 64)) : null };
        this.paint(v);
        this.place(v);
        list.push(v);
        if (list.length >= 14) break;
      }
      if (list.length >= 14) break;
    }
    this.parked.set(t.key, list);
  }

  removeTile(t) {
    const list = this.parked.get(t.key);
    if (!list) return;
    for (const v of list) this.release(v.type, v.slot);
    this.parked.delete(t.key);
  }

  // Sets a vehicle on the ground, tilted to the slope: its own mesh if it has one (the one being
  // driven), else its instance slot.
  place(v) {
    const h = this.map.heightmap;
    const y = h.heightAt(v.x, v.z);
    const fx = Math.sin(v.yaw);
    const fz = Math.cos(v.yaw);
    const ahead = h.heightAt(v.x + fx * 1.5, v.z + fz * 1.5);
    const behind = h.heightAt(v.x - fx * 1.5, v.z - fz * 1.5);
    const right = h.heightAt(v.x - fz, v.z + fx);
    const left = h.heightAt(v.x + fz, v.z - fx);
    const o = v.mesh || tmp;
    o.position.set(v.x, y + 0.02 + surfaceLift(this.chunks.surfaceAt(v.x, v.z)), v.z);
    o.rotation.set(0, 0, 0);
    o.rotateY(v.yaw);
    o.rotateX(-Math.atan2(ahead - behind, 3));
    o.rotateZ(Math.atan2(right - left, 2) + (v.lean || 0));
    if (!v.mesh) {
      o.updateMatrix();
      this.pool[v.type].set(v.slot, o.matrix);
      this.placeWheels(v, o.matrix);
    } else this.turnWheels(v);
    v.y = y;
  }

  // Adds the parked vehicles near a point to a list of things to bump into (the traffic adds
  // its own through Traffic.collect).
  collect(x, z, list) {
    for (const vehicles of this.parked.values()) {
      for (const v of vehicles) {
        if (Math.abs(v.x - x) > 10 || Math.abs(v.z - z) > 10) continue;
        const r = v.type === 'bike' ? 0.5 : (VEHICLES[v.type] || VEHICLES.taxi).radius * 0.9;
        list.push({ p: [v.x - r, v.z - r, v.x + r, v.z - r, v.x + r, v.z + r, v.x - r, v.z + r], minX: v.x - r, minZ: v.z - r, maxX: v.x + r, maxZ: v.z + r, top: (v.y || 0) + 1.6 });
      }
    }
    return list;
  }

  // The parked vehicle nearest a point within reach, or null.
  nearest(x, z, reach) {
    let best = null;
    let bestD = reach * reach;
    for (const list of this.parked.values()) {
      for (const v of list) {
        const d = (v.x - x) ** 2 + (v.z - z) ** 2;
        if (d < bestD) {
          bestD = d;
          best = v;
        }
      }
    }
    return best;
  }

  enter(v) {
    for (const list of this.parked.values()) {
      const k = list.indexOf(v);
      if (k >= 0) list.splice(k, 1);
    }
    // the driven vehicle gets a mesh of its own; its parking slot is given back
    this.release(v.type, v.slot);
    const mesh = vehicleMesh(v.type, v.paint || null);
    this.scene.add(mesh);
    this.driving = { type: v.type, x: v.x, z: v.z, yaw: v.yaw, mesh, def: VEHICLES[v.type], speed: 0, lean: 0, spin: v.spin || 0, steer: 0, paint: v.paint || null };
    this.place(this.driving);
    if (this.physics && this.physics.ready) this.physics.enter(v.type, v.x, this.driving.y, v.z, v.yaw);
    return this.driving;
  }

  // Leaves the vehicle parked where it stands. Returns where the driver steps out.
  exit() {
    const v = this.driving;
    this.driving = null;
    const key = `${Math.floor(v.x / TILE)}_${Math.floor(v.z / TILE)}`;
    if (!this.parked.has(key)) this.parked.set(key, []);
    this.scene.remove(v.mesh);
    if (this.physics) this.physics.exit();
    const parked = { type: v.type, x: v.x, z: v.z, yaw: v.yaw, spin: v.spin || 0, slot: this.pool[v.type].add(), paint: v.paint || null };
    this.parked.get(key).push(parked);
    this.paint(parked);
    this.place(parked);
    // out on the left, the driver's side
    return { x: v.x + Math.cos(v.yaw) * (v.def.radius + 0.9), z: v.z - Math.sin(v.yaw) * (v.def.radius + 0.9) };
  }

  // move: { x: steer right, z: throttle }, each -1..1. brake: handbrake held.
  drive(dt, move, brake) {
    const v = this.driving;
    const def = v.def;
    const surface = this.chunks.surfaceAt(v.x, v.z);
    if (this.physics && this.physics.ready) return this.driveWithPhysics(v, dt, move, brake, surface);
    const top = surface === 0 ? def.tar : surface === 1 ? def.earth : def.rough;
    if (move.z > 0) v.speed += def.accel * dt * (v.speed < 0 ? 2 : 1);
    else if (move.z < 0) v.speed -= (v.speed > 0.5 ? def.brake : def.accel * 0.6) * dt;
    else v.speed -= Math.sign(v.speed) * Math.min(Math.abs(v.speed), 2.2 * dt);
    if (brake) v.speed -= Math.sign(v.speed) * Math.min(Math.abs(v.speed), def.brake * 1.3 * dt);
    // rough ground drags the speed down rather than capping it at once
    if (v.speed > top) v.speed = Math.max(top, v.speed - 16 * dt);
    if (v.speed < -top * 0.3) v.speed = -top * 0.3;
    // uphill is slower
    const fx = Math.sin(v.yaw);
    const fz = Math.cos(v.yaw);
    const h = this.map.heightmap;
    const grade = (h.heightAt(v.x + fx * 2, v.z + fz * 2) - h.heightAt(v.x, v.z)) / 2;
    v.speed -= grade * 9 * dt;

    const grip = Math.min(1, Math.abs(v.speed) / 4);
    const turn = -move.x * def.turn * grip * (v.speed < 0 ? -1 : 1) / (1 + Math.abs(v.speed) * 0.05);
    v.yaw += turn * dt;
    v.lean = v.type === 'bike' ? -turn * 0.22 * grip : 0;
    v.steer = -move.x * STEER;
    this.roll(v, dt);
    v.x += Math.sin(v.yaw) * v.speed * dt;
    v.z += Math.cos(v.yaw) * v.speed * dt;
    const b = this.map.meta.bounds;
    v.x = Math.max(b.x0 + 5, Math.min(b.x1 - 5, v.x));
    v.z = Math.max(b.z0 + 5, Math.min(b.z1 - 5, v.z));
    const blocking = this.chunks.collidersNear(v.x, v.z, this.hits);
    if (this.obstacles) this.obstacles(v.x, v.z, blocking);
    const fixed = resolveCircle(v.x, v.z, def.radius, blocking);
    if (fixed.hit) {
      v.x = fixed.x;
      v.z = fixed.z;
      v.speed *= 0.5;
    }
    this.place(v);
    return v;
  }

  // The physics worker moves the vehicle; this sends it the controls and takes its newest pose.
  driveWithPhysics(v, dt, move, brake, surface) {
    const grip = surface === 0 ? 1 : surface === 1 ? 0.72 : 0.5;
    this.physics.input(move.z, -move.x, 0, brake ? 1 : 0, grip, dt);
    const s = this.physics.state;
    if (!s) return v;
    v.x = s.x;
    v.y = s.y;
    v.z = s.z;
    v.speed = s.speed;
    v.steer = -move.x * STEER;
    this.roll(v, dt);
    this.turnWheels(v);
    const { yaw, roll } = yawRoll(s.qx, s.qy, s.qz, s.qw);
    v.yaw = yaw;
    const b = this.map.meta.bounds;
    v.x = Math.max(b.x0 + 5, Math.min(b.x1 - 5, v.x));
    v.z = Math.max(b.z0 + 5, Math.min(b.z1 - 5, v.z));
    v.mesh.position.set(v.x, v.y + 0.02, v.z);
    if (v.type === 'bike') {
      // the bike's body cannot roll in the simulation; it leans into the steering as it always did
      const grip = Math.min(1, Math.abs(v.speed) / 4);
      v.lean = move.x * 0.22 * grip;
      v.mesh.rotation.set(0, v.yaw, v.lean);
    } else {
      v.lean = roll;
      v.mesh.quaternion.set(s.qx, s.qy, s.qz, s.qw);
    }
    return v;
  }
}
